// @ts-expect-error - Import needs esModuleInterop flag
import React, { useState, ReactNode, ReactElement } from 'react'
import { Controller, Control } from 'react-hook-form'

import {
  FormControl,
  TextField,
  TextFieldProps,
  SxProps,
  Theme
} from '@mui/material'
import { DesktopDatePicker, DesktopDatePickerProps } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enUS, fr, de, it, es, nl, pt } from 'date-fns/locale'
// @ts-expect-error - Import needs esModuleInterop flag
import { useTranslation } from '@contexts/translation'
import { SelectSkeleton } from '@components/common/Skeletons'

// Locale type with placeholder
interface LocaleInfo {
  locale: Locale
  placeholder: string
}

// Map of locales with placeholder formatting
const localeMap: Record<string, LocaleInfo> = {
  en: { locale: enUS, placeholder: 'dd/mm/yyyy' },
  fr: { locale: fr, placeholder: 'jj/mm/aaaa' },
  de: { locale: de, placeholder: 'tt/mm/jjjj' },
  it: { locale: it, placeholder: 'gg/mm/aaaa' },
  es: { locale: es, placeholder: 'dd/mm/aaaa' },
  nl: { locale: nl, placeholder: 'dd/mm/jjjj' },
  pt: { locale: pt, placeholder: 'jj/mm/aaaa' }
}

// Type for renderInput function to fix type error
type RenderInputFn = (params: any) => ReactElement

interface DatePickerProps {
  /** Field name in the form */
  name: string
  /** Label text for the field */
  label?: string
  /** React Hook Form control object */
  control: Control<any>
  /** Whether the field has an error */
  error?: boolean
  /** ID for the field */
  id?: string
  /** Error message to display */
  errorMessage?: string
  /** Whether the field takes up the full width */
  fullWidth?: boolean
  /** Whether the field is required */
  required?: boolean
  /** Whether the field is disabled */
  disabled?: boolean
  /** Whether the field can be cleared */
  clearable?: boolean
  /** Custom width for the field */
  width?: string | number
  /** Margin size */
  margin?: 'none' | 'dense' | 'normal'
  /** Form control size */
  size?: 'small' | 'medium'
  /** Text field size */
  textFieldSize?: 'small' | 'medium'
  /** Date input format */
  inputFormat?: string
  /** Default value for the field */
  defaultValue?: Date | null
  /** Whether the field is loading */
  loading?: boolean
  /** Function to determine if a date should be disabled */
  shouldDisableDate?: (date: Date) => boolean | null
  /** Custom render function for the input */
  customRenderInput?: RenderInputFn
  /** Test ID for the component */
  data_testid?: string
  /** Props to pass to the input component */
  InputProps?: Partial<TextFieldProps['InputProps']>
  /** Custom components object for date picker */
  components?: DesktopDatePickerProps<any, any>['components']
  /** Additional props */
  [key: string]: any
}

/**
 * DatePicker component
 * A form date picker component that works with React Hook Form
 */
const DatePicker = ({
  name,
  label,
  control,
  error,
  id,
  errorMessage,
  fullWidth,
  required,
  disabled,
  clearable,
  width,
  margin = 'normal',
  size = 'medium',
  textFieldSize = 'small',
  inputFormat = 'dd/MM/yyyy',
  defaultValue = null,
  loading = false,
  shouldDisableDate = () => null,
  customRenderInput = null,
  data_testid,
  InputProps = {},
  components,
  ...props
}: DatePickerProps): JSX.Element => {
  const [value, setValue] = useState<Date | null>(null)
  const { lang } = useTranslation()

  return (
    <>
      {!loading && (
        <FormControl
          {...props}
          margin={margin}
          size={size}
          style={{ width: fullWidth ? '100%' : width || '195px' }}
        >
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            render={({ field }) => (
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                locale={localeMap[lang]?.locale}
              >
                <DesktopDatePicker
                  views={['year', 'month', 'day']}
                  value={value}
                  disabled={disabled}
                  inputFormat={inputFormat}
                  clearable={clearable}
                  onChange={(newValue: Date | null) => {
                    setValue(newValue)
                  }}
                  shouldDisableDate={shouldDisableDate}
                  InputProps={InputProps}
                  {...(components && { components })}
                  {...field}
                  renderInput={
                    customRenderInput
                      ? customRenderInput
                      : (((params: any) => (
                          <TextField
                            {...params}
                            data-testid={data_testid}
                            inputProps={{
                              ...params.inputProps,
                              placeholder: localeMap[lang]?.placeholder
                            }}
                            label={label}
                            id={id}
                            disabled={disabled}
                            name={name}
                            error={error}
                            helperText={errorMessage}
                            required={required}
                            fullWidth
                            size={textFieldSize}
                            style={{
                              backgroundColor: '#fff'
                            }}
                            // sx={{
                            //   '& .MuiFilledInput-input': {
                            //     paddingTop: label ? 2 : 1.5,
                            //     paddingBottom: label ? 1 : 1.5
                            //   }
                            // }}
                          />
                        )) as RenderInputFn)
                  }
                  minDate={new Date(1970, 0, 1)}
                  maxDate={new Date(2100, 11, 31)}
                />
              </LocalizationProvider>
            )}
          />
        </FormControl>
      )}
      {loading && <SelectSkeleton />}
    </>
  )
}

export default DatePicker
